import React, { useContext, Suspense, lazy } from "react";
import { ThemeProvider as StyledComponentsThemeProvider } from "styled-components";
import { ThemeProvider as MuiComponentsThemeProvider } from '@mui/material/styles';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { lightTheme, darkTheme, createMuiTheme } from './styles/theme';
import { GlobalStyle, MuiGlobalStyles } from './styles/global.styles';
import { ThemeProviderContext } from "./context/theme.context";
import { SnackbarProvider } from "./context/snackbar.context";

import { Helmet } from "react-helmet";
import Dashboard from "./views/dashboard/dashboard.screen";
import { useAuth } from "./context/auth.context";
import { AccountabilityProvider } from "./context/accountability.context";
import Loader from "./components/loader/loader.component";
import ProtectedRoute from "./components/protected-route/protected-route.component";
import RouteErrorBoundary from "./components/error-boundary/route-error-boundary.component";

const Landing = lazy(() => import("./views/landing/landing.screen"));
const OnboardingPage = lazy(() => import("./pages/onboarding/onboarding.page"));
const TermsPage = lazy(() => import("./pages/legal/terms.page"));
const PrivacyPage = lazy(() => import("./pages/legal/privacy.page"));

/**
 * Fallback loader for lazy-loaded routes.
 */
const LazyFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
    }}
    role="status"
    aria-label="Loading"
  >
    <CircularProgress size={60} />
  </Box>
);

const App = () => {
  const { isAuthenticated, loading } = useAuth();
  const { theme } = useContext(ThemeProviderContext);
  const currentTheme = theme === 'light' ? lightTheme : darkTheme;
  const muiTheme = createMuiTheme(currentTheme);

  return (
    <StyledComponentsThemeProvider theme={currentTheme}>
      <MuiComponentsThemeProvider theme={muiTheme}>
        <SnackbarProvider>
          <Helmet>
            <title>FitSocial</title>
          </Helmet>
          <GlobalStyle />
          <MuiGlobalStyles />
          {loading ? (
            <Loader />
          ) : (
            <Suspense fallback={<LazyFallback />}>
              <Routes>
                {/* Public routes - Landing page with auth modals */}
                <Route path="/" element={
                  isAuthenticated ? <Navigate to="/home" replace /> : (
                    <RouteErrorBoundary>
                      <Landing />
                    </RouteErrorBoundary>
                  )
                } />
                <Route path="/login" element={
                  isAuthenticated ? <Navigate to="/home" replace /> : (
                    <RouteErrorBoundary>
                      <Landing />
                    </RouteErrorBoundary>
                  )
                } />
                <Route path="/signup" element={
                  isAuthenticated ? <Navigate to="/home" replace /> : (
                    <RouteErrorBoundary>
                      <Landing />
                    </RouteErrorBoundary>
                  )
                } />

                {/* Public legal pages - accessible without authentication */}
                <Route path="/terms" element={
                  <RouteErrorBoundary>
                    <TermsPage />
                  </RouteErrorBoundary>
                } />
                <Route path="/privacy" element={
                  <RouteErrorBoundary>
                    <PrivacyPage />
                  </RouteErrorBoundary>
                } />

                {/* Onboarding - full-screen, outside dashboard shell */}
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary>
                      <OnboardingPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* Protected routes - Dashboard with nested routing */}
                <Route path="/*" element={
                  <ProtectedRoute>
                    <AccountabilityProvider>
                      <Dashboard />
                    </AccountabilityProvider>
                  </ProtectedRoute>
                } />
              </Routes>
            </Suspense>
          )}
        </SnackbarProvider>
      </MuiComponentsThemeProvider>
    </StyledComponentsThemeProvider>
  );
};

export default App;
